import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Descendant } from 'slate';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { fetchImageFromS3AndConvertToBase64 } from '../../../utils/s3';
import { regenerateIcon } from '../../../assets/hostedassets';
import { TIME_DATE_FORMAT } from '../../../utils/constants/commonConstants';
import { closeCommentsThread, resetAndHideCommentsThread } from '../../../store/commentsThread/commentsThread_actions';
import { convertToSnakeCase, getSubdomain } from '../../../utils/utils';
import { Mixpanel } from '../../../utils/mixpanel';
import { toast } from 'react-toastify';

import { Button, Modal } from '../../_custom';
import LoadingSpinner from '../../_custom/LoadingSpinner/LoadingSpinner';
import TextEditor from '../../TextEditor/TextEditor';

import styles from './EngagementLetterEditor.module.scss';
import { AICA_ENGAGEMENT_LETTER_PREVIEW, AICA_ENGAGEMENT_LETTER_SAVE } from '../../../utils/constants/mixpanelEvents/investorEvents';
import { TEXT_EDITOR_TYPES } from '../../../store/investor/constants';
import { pdf } from '@react-pdf/renderer';
import ReactPdfDocument from '../../TextEditor/print/ReactPdfDocument';
import { sanitizeSlateValue } from '../../TextEditor/utils/SlateSerialisationUtil';

function EngagementLetterEditor({
    source,
    openModal,
    orgId,
    orgName,
    generating,
    editorInitialData,
    editorReady,
    savingData,
    saveEngagementLetter,
    setShowRegenConfirmModal,
    userInfo,
}) {
    const dispatch = useAppDispatch();
    const [saving, setSaving] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const contentToPrint = useRef(null);
    const editorContent = useRef<Descendant[]>([]);
    const domainMetadata = useAppSelector((state) => state.user.domainMetadata);
    const mainImageUrl = domainMetadata?.mainImageUrl;
    const [triggerSave, setTriggerSave] = useState(false);
    const user = useAppSelector((state) => state.user.userData);
    const [logoBase64, setLogoBase64] = useState<string | undefined>(undefined);

    useEffect(() => {
        dispatch(resetAndHideCommentsThread());
    }, []);

    useEffect(() => {
        if (triggerSave) {
            setTriggerSave(false);
        }
    }, [triggerSave, editorContent, orgId]);

    // Load logo on mount
    useEffect(() => {
        const loadLogo = async () => {
            if (mainImageUrl) {
                try {
                    const response = await fetchImageFromS3AndConvertToBase64(mainImageUrl);
                    if (response) {
                        setLogoBase64(response as string);
                    }
                } catch (err) {
                    // Error loading logo - fail silently
                }
            }
        };
        loadLogo();
    }, [mainImageUrl]);

    const handlePreviewClick = async (e) => {
        e.preventDefault();
        setLoadingPreview(true);

        Mixpanel.track(AICA_ENGAGEMENT_LETTER_PREVIEW, {
            userRole: user?.accountType,
            pnoPlatform: getSubdomain(),
            userId: user?.userId,
            currentUrl: window.location.href,
            organizationId: orgId,
        });

        try {
            // Sanitize content before PDF generation to handle old data structures
            const rawContent = editorContent.current?.length ? editorContent.current : editorInitialData ?? [];
            const content = sanitizeSlateValue(rawContent);

            if (!content || content.length === 0) {
                toast.error('No content to preview');
                setLoadingPreview(false);
                return;
            }

            const pdfDocument = (
                <ReactPdfDocument
                    value={content}
                    pageSize="A3"
                    logoBase64={logoBase64}
                    orgName={orgName}
                    companyName={orgName ?? 'Company'}
                    includeWatermark={false}
                    showFooter={false}
                    documentType="engagementLetter"
                />
            );

            const blob = await pdf(pdfDocument).toBlob();

            const url = URL.createObjectURL(blob);
            const newTab = window.open(url, '_blank');

            if (!newTab) {
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.click();
            }

            // Increase timeout to allow for slower connections/larger PDFs
            setTimeout(() => URL.revokeObjectURL(url), 120000);
        } catch (err) {
            toast.error('Error generating PDF preview');
        } finally {
            setLoadingPreview(false);
        }
    };

    const handleClose = () => {
        dispatch(closeCommentsThread());
        setTriggerSave(true);
        saveEngagementLetter(editorContent.current);
    };

    const userMessage = useMemo(() => {
        if (!userInfo?.userName || !userInfo?.onePagerData?.lastUpdatedAt) {
            return '';
        }
        const formattedDate = dayjs(userInfo.onePagerData.lastUpdatedAt).format(TIME_DATE_FORMAT);

        return `Last edited on ${formattedDate} by ${userInfo.userName}`;
    }, [userInfo]);

    const handleSaveAndClose = (e) => {
        e.preventDefault();
        setSaving(true);
        setTriggerSave(true);
        dispatch(closeCommentsThread());
        saveEngagementLetter(editorContent.current);
        downloadPDF(orgName);

        Mixpanel.track(AICA_ENGAGEMENT_LETTER_SAVE, {
            userRole: user?.accountType,
            pnoPlatform: getSubdomain(),
            userId: user?.userId,
            currentUrl: window.location.href,
            organizationId: orgId,
        });
    };

    const downloadPDF = async (companyName: string) => {
        try {
            // Sanitize content before PDF generation to handle old data structures
            const rawContent =
                editorContent.current?.length > 0
                    ? editorContent.current
                    : editorInitialData?.length
                    ? editorInitialData
                    : [{ type: 'paragraph' as const, children: [{ text: '' }] }];
            const content = sanitizeSlateValue(rawContent);

            const currentOrgName = companyName || orgName || 'Company';
            const finalFileName = currentOrgName.replace(/[^a-zA-Z0-9]/g, '_');
            const fileName = `Engagement_Letter_${finalFileName}.pdf`;

            // Generate PDF using @react-pdf/renderer
            const pdfDocument = (
                <ReactPdfDocument
                    value={content}
                    pageSize="A3"
                    logoBase64={logoBase64}
                    orgName={orgName}
                    companyName={currentOrgName}
                    includeWatermark={false}
                    showFooter={false}
                    documentType="engagementLetter"
                />
            );

            const blob = await pdf(pdfDocument).toBlob();

            // Download the PDF
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(url), 60000);

            setSaving(false);
        } catch (err) {
            toast.error('Error generating PDF');
            setSaving(false);
        }
    };

    // Add loading state UI for regeneration
    const renderRegenerateButton = () => {
        return (
            <div
                className={styles.Regen}
                onClick={() => {
                    !generating && setShowRegenConfirmModal(true);
                }}
            >
                {generating ? (
                    <>
                        <LoadingSpinner color="var(--primary-text-colour)" height="18px" style={{ margin: 0 }} />
                        <span>Regenerating</span>
                    </>
                ) : (
                    <>
                        <img src={regenerateIcon} alt={'re'} height={'24px'} />
                        <span>Regenerate</span>
                    </>
                )}
            </div>
        );
    };

    return openModal ? (
        <>
            <Modal
                size="full"
                open={openModal}
                style={{ padding: 0, fontSize: '12px', lineHeight: '140%', position: 'absolute' }}
                persistent
                withoutCloseButton
            >
                <div className={styles.Container}>
                    <div className={styles.Top}>
                        {!!editorReady && (
                            <TextEditor
                                source={source}
                                editorInitialData={editorInitialData}
                                targetRef={contentToPrint}
                                editorContentRef={editorContent}
                                assesseeOrgId={orgId}
                                generating={generating}
                                onClose={handleClose}
                                savingData={savingData}
                                editorType={TEXT_EDITOR_TYPES.ENGAGEMENT_LETTER}
                            />
                        )}
                        <div className={styles.Footer}>
                            <div>{renderRegenerateButton()}</div>

                            <div className={styles.Text}>*Right-click tables to modify: add rows, add columns, or delete</div>

                            {userMessage && <div className={styles.Text}>{userMessage}</div>}

                            <div className={styles.Buttons}>
                                <Button
                                    text="Preview"
                                    variant="purpleinverted"
                                    onClick={handlePreviewClick}
                                    style={{ margin: '0 20px 0 0 ' }}
                                    loading={loadingPreview}
                                />
                                <Button
                                    text="Save & Download"
                                    variant="primary"
                                    onClick={handleSaveAndClose}
                                    style={{ marginTop: '0' }}
                                    loading={saving}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.Comments} id="el-comment-container">
                        {/* //Container Element Where comments thread is rendered via portal */}
                    </div>
                </div>
            </Modal>
        </>
    ) : (
        <></>
    );
}

export default EngagementLetterEditor;
